// The investigation video page body: the shared video view fed by this
// file's leads (with video anchors) and its event log, with the detective's
// watch-for brief and the footage summary above the points list.
import { useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import cn from "classnames";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import Button from "../../common/Button";
import AiDisclaimer from "../AiDisclaimer";
import VideoView from "../../common/video/VideoView";
import { VideoPoint, eventToPoint, leadToPoint, mergePoints } from "../../common/video/types";
import { api, FileDetailData } from "../api";
import { describeProgress } from "../../Editor/FootageProgress";
import { useStaffView } from "../StaffView";

interface Props {
  investigationId: string;
  file: FileDetailData;
  initialSeekMs?: number;
  focusLeadId?: string | null;
  onLeadStatus: (leadId: string, status: string, reason?: string) => void;
  onRerun: () => void;
  rerunning: boolean;
  onFileChange: (next: FileDetailData) => void;
}

const DOC_TYPE_LABEL: Record<string, string> = { bodycam: "Body cam", surveillance: "Surveillance", cellphone: "Cell-phone video", dashcam: "Dash cam", interview_room: "Interview room", other: "Video" };

const VideoFileViewer: React.FC<Props> = ({ investigationId, file, initialSeekMs, focusLeadId, onLeadStatus, onRerun, rerunning, onFileChange }) => {
  const video = file.video;
  // Staff portal view is read-only: show the detective's brief, drop the
  // editor and the re-run (the server refuses both).
  const staffView = Boolean(useStaffView());
  const [watchFor, setWatchFor] = useState(video?.watchFor || "");
  const [analyzeAll, setAnalyzeAll] = useState(Boolean(video?.analyzeAll));
  const [saving, setSaving] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(true);
  const dirty = watchFor !== (video?.watchFor || "") || analyzeAll !== Boolean(video?.analyzeAll);

  const points = useMemo<VideoPoint[]>(() => {
    const leads = file.leads.map(leadToPoint).filter((p): p is VideoPoint => Boolean(p));
    const events = (video?.events || []).map(eventToPoint);
    return mergePoints(leads, events);
  }, [file.leads, video?.events]);

  const durationMs = Math.round((video?.durationSec || 0) * 1000) || file.transcript?.durationMs || 0;
  const frameUrl = (t: number) => api.frameUrl(investigationId, file.id, t);

  const save = async () => {
    setSaving(true);
    try {
      const row = await api.setVideoOptions(investigationId, file.id, { watchFor, analyzeAll });
      onFileChange({ ...file, metadata: row.metadata, video: video ? { ...video, watchFor: watchFor || undefined, analyzeAll } : video });
      toast.success("Saved. Re-run the analysis to apply it.");
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const working = video && video.status !== "completed" && video.status !== "failed";
  const aside = (
    <div className="border-b border-base-300 p-3 space-y-3 text-sm max-h-[45%] overflow-y-auto">
      {video?.status === "failed" && (
        <div className="rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 p-2 text-red-800 dark:text-red-400 text-[13px]">{video.error || "Footage analysis failed."}</div>
      )}
      {working && video && (() => {
        const d = describeProgress({ fileId: file.id, fileName: file.original_file_name, status: video.status, error: video.error, durationSec: video.durationSec, progress: video.progress || null, eventCount: video.eventCount ?? null });
        return (
          <div>
            <p className="text-base-content/60 text-[13px]">
              <span className="inline-block size-2 rounded-full bg-amber-500 animate-pulse mr-1.5" />
              {d.label}
              {d.detail ? ` · ${d.detail}` : ""}
              {d.pct != null ? ` · ${d.pct}%` : ""}
            </p>
            {d.pct != null && (
              <div className="mt-1.5 h-1.5 rounded-full bg-base-300 overflow-hidden">
                <div className="h-full rounded-full bg-blue-500 transition-[width] duration-700 ease-out" style={{ width: `${Math.max(2, d.pct)}%` }} />
              </div>
            )}
            <p className="mt-1 text-[11px] text-base-content/50">Points of interest appear when it finishes.</p>
          </div>
        );
      })()}
      {video?.summary && (
        <div>
          <button type="button" className="w-full flex items-center gap-1 font-semibold text-base-content" onClick={() => setSummaryOpen((o) => !o)}>
            {DOC_TYPE_LABEL[video.documentType || "other"] || "Video"} summary
            <ChevronDownIcon className={cn("size-3.5 text-base-content/50 transition-transform ml-auto", summaryOpen && "rotate-180")} />
          </button>
          <div className={cn("grid transition-[grid-template-rows] duration-300 ease-in-out", summaryOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]")}>
            <div className="min-h-0 overflow-hidden">
              <AiDisclaimer kind="documentSummary" size="sm" className="my-1.5" />
              <p className="text-[13px] leading-5 text-base-content/80 whitespace-pre-wrap">{video.summary}</p>
              {video.conditions && (video.conditions.lighting || video.conditions.weather || video.conditions.location_type) && (
                <p className="mt-1 text-[11px] text-base-content/50">{[video.conditions.location_type, video.conditions.lighting, video.conditions.weather].filter(Boolean).join(" · ")}</p>
              )}
            </div>
          </div>
        </div>
      )}
      {staffView ? (
        (video?.watchFor || video?.analyzeAll) && (
          <div>
            <p className="text-xs font-semibold text-base-content/70 mb-1">Watch for</p>
            {video?.watchFor && <p className="text-[13px] leading-5 text-base-content/80 whitespace-pre-wrap">{video.watchFor}</p>}
            {video?.analyzeAll && <p className="mt-1 text-[11px] text-base-content/50">Set to read every minute frame by frame.</p>}
          </div>
        )
      ) : (
      <div>
        <label className="block text-xs font-semibold text-base-content/70 mb-1">Watch for</label>
        <textarea
          value={watchFor}
          onChange={(e) => setWatchFor(e.target.value)}
          rows={2}
          placeholder="e.g. white Tahoe, partial plate 7ABC; male in red hoodie"
          className="textarea textarea-bordered textarea-sm w-full text-[13px] leading-5 bg-base-100"
        />
        <label className="label cursor-pointer justify-start gap-2 py-1">
          <input type="checkbox" className="checkbox checkbox-sm [--chkbg:theme(colors.blue.500)] [--chkfg:white] checked:border-blue-500" checked={analyzeAll} onChange={(e) => setAnalyzeAll(e.target.checked)} />
          <span className="text-xs text-base-content/70">Read every minute frame by frame (slower, costs more)</span>
        </label>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="xs" disabled={!dirty || saving} onClick={save}>
            {saving ? "Saving…" : "Save"}
          </Button>
          <Button variant="outline" size="xs" disabled={rerunning || Boolean(working)} onClick={onRerun} title="Re-read the footage with the brief above">
            {rerunning ? "Starting…" : "Re-run analysis"}
          </Button>
        </div>
      </div>
      )}
    </div>
  );

  return (
    <VideoView
      src={file.url}
      posterUrl={video?.frames?.length ? frameUrl(video.frames[Math.min(5, video.frames.length - 1)]) : undefined}
      durationMs={durationMs}
      points={points}
      idle={video?.idle}
      transcript={file.transcript || null}
      participants={file.participants}
      frameUrl={frameUrl}
      initialSeekMs={initialSeekMs}
      focusPointId={focusLeadId ? `lead:${focusLeadId}` : null}
      onLeadStatus={onLeadStatus}
      transcriptScrollKey={`inv.video.${file.id}`}
      listTitle="Points of interest"
      listEmptyText={working ? "Reading the footage…" : "Nothing flagged in this footage."}
      aside={aside}
    />
  );
};

export default VideoFileViewer;
